import React, { useCallback, useMemo } from 'react';
import CallHistoryListItemV2 from 'flow/components/call-history-v2/CallHistoryListItemV2';
import { firstIndexToFocus } from 'flow/components/nav/list-keys/list-key-action';
import { CallRecordKey } from 'flow/types/EntityKeys';
import {
    CallHistoryRow,
    callHistoryDates,
    callHistoryRowKey,
    rowIsFocusable,
    rowOpensCallRecord,
} from './call-history-rows';
import DateGroupedList, { DateGroupedRowProps } from './DateGroupedList';
import { type DateGroups } from './types';

interface Props {
    dateGroups: DateGroups | null;
    selectedCallRecordKey?: CallRecordKey;
    bgClassName?: string;
}

const DateGroupedCallRecordList = ({
    dateGroups,
    selectedCallRecordKey,
    bgClassName,
}: Props) => {
    const dates = useMemo(() => callHistoryDates(dateGroups), [dateGroups]);

    // Tab enters at the row whose call record page is open, else at the
    // first row.
    const selectedOrFirstIndex = useCallback(
        (rows: CallHistoryRow[]) => {
            const selectedIndex = rows.findIndex((row) =>
                rowOpensCallRecord(row, selectedCallRecordKey),
            );
            return selectedIndex === -1
                ? firstIndexToFocus(rows, rowIsFocusable)
                : selectedIndex;
        },
        [selectedCallRecordKey],
    );

    // A row's own control is its link to the call, or its first button
    // when it has no link, as a voicemail without a call record.
    const renderRow = (row: CallHistoryRow, rowProps: DateGroupedRowProps) => (
        <CallHistoryListItemV2
            callRecordKey={
                row.kind === 'callRecord' ? row.callRecordKey : undefined
            }
            voiceMessage={
                row.kind === 'voiceMessage' ? row.voiceMessage : undefined
            }
            isFocusable={rowIsFocusable(row)}
            isSelected={rowOpensCallRecord(row, selectedCallRecordKey)}
            {...rowProps}
        />
    );

    return (
        <DateGroupedList
            dates={dates}
            rowKey={callHistoryRowKey}
            rowIsFocusable={rowIsFocusable}
            entryIndex={selectedOrFirstIndex}
            renderRow={renderRow}
            bgClassName={bgClassName}
        />
    );
};

export default DateGroupedCallRecordList;
